import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { Link } from "react-router-dom";
import { auth } from "./firebaseConfig";
import "./userPayment.css";

const API_URL = "https://api.malidag.com";

function UserPayment() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [currencyFilter, setCurrencyFilter] =
    useState("all");

  /*
  |--------------------------------------------------------------------------
  | Load Stripe orders
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    let cancelled = false;

    const loadOrders = async () => {
      try {
        setLoading(true);
        setError("");

        const user = auth.currentUser;

        if (!user) {
          throw new Error(
            "Admin authentication is required."
          );
        }

        const token = await user.getIdToken();

        const response = await fetch(
          `${API_URL}/api/admin/stripe/orders`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error(
              "Your admin session has expired."
            );
          }

          if (response.status === 403) {
            throw new Error(
              "This account does not have admin access."
            );
          }

          throw new Error(
            `Failed to load payments (${response.status}).`
          );
        }

        const data = await response.json();

        if (!cancelled) {
          setOrders(
            Array.isArray(data?.orders)
              ? data.orders
              : []
          );
        }
      } catch (err) {
        console.error(
          "Failed to load Stripe payments:",
          err
        );

        if (!cancelled) {
          setError(
            err?.message ||
              "Failed to load payments."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadOrders();

    return () => {
      cancelled = true;
    };
  }, []);

  /*
  |--------------------------------------------------------------------------
  | Helpers
  |--------------------------------------------------------------------------
  */

  const getOrderDate = (order) => {
    const value =
      order?.paidAt ||
      order?.createdAt ||
      order?.updatedAt;

    if (!value) {
      return null;
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime())
      ? null
      : date;
  };

  const formatDate = (order) => {
    const date = getOrderDate(order);

    if (!date) {
      return "Unknown date";
    }

    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (order) => {
    const date = getOrderDate(order);

    if (!date) {
      return "";
    }

    return date.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatMoney = (amount, currency) => {
    const numericAmount = Number(amount);

    if (!Number.isFinite(numericAmount)) {
      return "—";
    }

    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: String(
          currency || "USD"
        ).toUpperCase(),
      }).format(numericAmount);
    } catch {
      return `${numericAmount.toFixed(2)} ${
        currency || ""
      }`;
    }
  };

  const shortenReference = (value) => {
    const reference = String(value || "");

    if (!reference) {
      return "—";
    }

    if (reference.length <= 22) {
      return reference;
    }

    return `${reference.slice(
      0,
      12
    )}...${reference.slice(-6)}`;
  };

  /*
  |--------------------------------------------------------------------------
  | Filters
  |--------------------------------------------------------------------------
  */

  const currencies = useMemo(() => {
    return [
      ...new Set(
        orders
          .map((order) =>
            String(
              order?.currency || ""
            ).toUpperCase()
          )
          .filter(Boolean)
      ),
    ].sort();
  }, [orders]);

  const filteredOrders = useMemo(() => {
    const query = search
      .trim()
      .toLowerCase();

    return orders.filter((order) => {
      const currency = String(
        order?.currency || ""
      ).toUpperCase();

      if (
        currencyFilter !== "all" &&
        currency !== currencyFilter
      ) {
        return false;
      }

      if (!query) {
        return true;
      }

      const searchable = [
        order?.delivery?.fullName,
        order?.delivery?.email,
        order?.stripePaymentIntentId,
        order?.userId,
        order?.delivery?.country,
        order?.delivery?.town,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(query);
    });
  }, [
    orders,
    search,
    currencyFilter,
  ]);

  /*
  |--------------------------------------------------------------------------
  | Dashboard statistics
  |--------------------------------------------------------------------------
  */

  const statistics = useMemo(() => {
  const customerIds = new Set();

  let canonicalRevenue = 0;
  let totalItems = 0;
  let totalBrandCost = 0;
  let hasIncompleteCostData = false;

  orders.forEach((order) => {
    if (order?.userId) {
      customerIds.add(order.userId);
    } else if (order?.delivery?.email) {
      customerIds.add(
        String(
          order.delivery.email
        ).toLowerCase()
      );
    }

    const usd = Number(
      order?.canonicalUsdAmount
    );

    if (Number.isFinite(usd)) {
      canonicalRevenue += usd;
    }

    if (Array.isArray(order?.items)) {
      order.items.forEach((item) => {
        const quantity =
          Number(item?.quantity);

        const safeQuantity =
          Number.isFinite(quantity) &&
          quantity > 0
            ? quantity
            : 0;

        totalItems += safeQuantity;

        const brandPrice =
          Number(item?.brandPrice);

        if (Number.isFinite(brandPrice)) {
          totalBrandCost +=
            brandPrice * safeQuantity;
        } else {
          hasIncompleteCostData = true;
        }
      });
    }
  });

  const grossProfit =
    hasIncompleteCostData
      ? null
      : canonicalRevenue -
        totalBrandCost;

  const grossMargin =
    grossProfit !== null &&
    canonicalRevenue > 0
      ? (grossProfit /
          canonicalRevenue) *
        100
      : null;

  return {
    transactions: orders.length,
    customers: customerIds.size,
    items: totalItems,
    canonicalRevenue,

    totalBrandCost:
      hasIncompleteCostData
        ? null
        : totalBrandCost,

    grossProfit,
    grossMargin,
  };
}, [orders]);

  /*
  |--------------------------------------------------------------------------
  | Loading / error
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <div className="payment-admin-state">
        <div className="payment-admin-spinner" />

        <h2>Loading payments</h2>

        <p>
          Retrieving Stripe transactions...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-admin-state payment-admin-error">
        <div className="payment-admin-state-icon">
          !
        </div>

        <h2>Unable to load payments</h2>

        <p>{error}</p>
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | UI
  |--------------------------------------------------------------------------
  */

  return (
    <div className="payment-admin-page">
      <div className="payment-admin-container">

        {/* HEADER */}

        <header className="payment-admin-header">
          <div>
            <div className="payment-admin-eyebrow">
              Malidag Admin
            </div>

            <h1>Payments</h1>

            <p>
              View finalized customer
              transactions processed through
              Stripe.
            </p>
          </div>

          <div className="stripe-provider-badge">
            <span className="stripe-provider-dot" />

            Stripe
          </div>
        </header>

        {/* STATISTICS */}

       <section className="payment-stat-grid">

  <div className="payment-stat-card">
    <span className="payment-stat-label">
      Revenue
    </span>

    <strong>
      {formatMoney(
        statistics.canonicalRevenue,
        "USD"
      )}
    </strong>

    <small>
      Canonical USD value
    </small>
  </div>

  <div className="payment-stat-card">
    <span className="payment-stat-label">
      Product cost
    </span>

    <strong>
      {statistics.totalBrandCost !== null
        ? formatMoney(
            statistics.totalBrandCost,
            "USD"
          )
        : "—"}
    </strong>

    <small>
      Brand / supplier cost
    </small>
  </div>

  <div className="payment-stat-card">
    <span className="payment-stat-label">
      Gross profit
    </span>

    <strong>
      {statistics.grossProfit !== null
        ? formatMoney(
            statistics.grossProfit,
            "USD"
          )
        : "—"}
    </strong>

    <small>
      Before fees and expenses
    </small>
  </div>

  <div className="payment-stat-card">
    <span className="payment-stat-label">
      Gross margin
    </span>

    <strong>
      {statistics.grossMargin !== null
        ? `${statistics.grossMargin.toFixed(2)}%`
        : "—"}
    </strong>

    <small>
      Product margin
    </small>
  </div>

  <div className="payment-stat-card">
    <span className="payment-stat-label">
      Transactions
    </span>

    <strong>
      {statistics.transactions}
    </strong>

    <small>
      Successful payments
    </small>
  </div>

  <div className="payment-stat-card">
    <span className="payment-stat-label">
      Customers
    </span>

    <strong>
      {statistics.customers}
    </strong>

    <small>
      Unique customers
    </small>
  </div>

  <div className="payment-stat-card">
    <span className="payment-stat-label">
      Items sold
    </span>

    <strong>
      {statistics.items}
    </strong>

    <small>
      Total quantity
    </small>
  </div>

</section>

        {/* TOOLBAR */}

        <section className="payment-toolbar">
          <div className="payment-search-wrapper">
            <span className="payment-search-icon">
              ⌕
            </span>

            <input
              type="search"
              value={search}
              onChange={(e) =>
                setSearch(e.target.value)
              }
              placeholder="Search customer, email or payment ID..."
              className="payment-search-input"
            />
          </div>

          <select
            className="payment-currency-select"
            value={currencyFilter}
            onChange={(e) =>
              setCurrencyFilter(
                e.target.value
              )
            }
          >
            <option value="all">
              All currencies
            </option>

            {currencies.map((currency) => (
              <option
                key={currency}
                value={currency}
              >
                {currency}
              </option>
            ))}
          </select>
        </section>

        {/* PAYMENT TABLE */}

        <section className="payment-table-card">
          <div className="payment-table-heading">
            <div>
              <h2>Transactions</h2>

              <p>
                {filteredOrders.length}{" "}
                {filteredOrders.length === 1
                  ? "payment"
                  : "payments"}
              </p>
            </div>
          </div>

          {filteredOrders.length === 0 ? (
            <div className="payment-empty-state">
              <div className="payment-empty-icon">
                $
              </div>

              <h3>
                No payments found
              </h3>

              <p>
                No Stripe transactions match
                your current search.
              </p>
            </div>
          ) : (
            <div className="payment-table-scroll">
              <table className="payment-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Payment</th>
                    <th>Market</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th />
                  </tr>
                </thead>

                <tbody>
                  {filteredOrders.map(
                    (order) => {
                      const paymentId =
                        order
                          ?.stripePaymentIntentId;

                      const customerName =
                        order?.delivery
                          ?.fullName ||
                        "Unknown customer";

                      const customerEmail =
                        order?.delivery
                          ?.email ||
                        "No email";

                      return (
                        <tr
                          key={
                            paymentId ||
                            order?._id
                          }
                        >
                          <td>
                            <div className="payment-customer">
                              <div className="payment-avatar">
                                {customerName
                                  .charAt(0)
                                  .toUpperCase()}
                              </div>

                              <div className="payment-customer-text">
                                <strong>
                                  {
                                    customerName
                                  }
                                </strong>

                                <span>
                                  {
                                    customerEmail
                                  }
                                </span>
                              </div>
                            </div>
                          </td>

                          <td>
                            <span
                              className="payment-reference"
                              title={
                                paymentId ||
                                ""
                              }
                            >
                              {shortenReference(
                                paymentId
                              )}
                            </span>
                          </td>

                          <td>
                            <span className="payment-market">
                              {String(
                                order?.countryCode ||
                                  "—"
                              ).toUpperCase()}
                            </span>
                          </td>

                          <td>
                            <div className="payment-amount">
                              <strong>
                                {formatMoney(
                                  order?.amount,
                                  order?.currency
                                )}
                              </strong>

                              <span>
                                {formatMoney(
                                  order?.canonicalUsdAmount,
                                  "USD"
                                )}{" "}
                                USD value
                              </span>
                            </div>
                          </td>

                          <td>
                            <span className="payment-status payment-status-paid">
                              <span />
                              Paid
                            </span>
                          </td>

                          <td>
                            <div className="payment-date">
                              <strong>
                                {formatDate(
                                  order
                                )}
                              </strong>

                              <span>
                                {formatTime(
                                  order
                                )}
                              </span>
                            </div>
                          </td>

                          <td className="payment-action-cell">
                            {paymentId ? (
                              <Link
                                className="payment-view-button"
                                to={`/user-payment-details/${encodeURIComponent(
                                  paymentId
                                )}`}
                                state={{
                                  order,
                                }}
                              >
                                View
                              </Link>
                            ) : (
                              <span>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default UserPayment;